import { useEffect } from "react";
import Lenis from "lenis";
import Header from "@/components/landing/layout/Header";
import Footer from "@/components/landing/layout/Footer";
import { useGSAP } from "@/hooks/useGSAP";

import ClaimsHowItWorks from "../components/ClaimsHowItWorks";
import NotCovered from "../components/NotCovered";
import FAQSection from "../components/FAQSection";
import PlanHero from "../components/PlanHero";
import BrandBar from "../components/BrandBar";
import CoverageCards from "../components/CoverageCards";
import WarrantyValue from "../components/WarrantyValue";
import HowToBuyPlan from "../components/HowToBuyPlan";

const ewCoverageItems = [
  {
    img: "/izicart/ew-motors-arms.jpg",
    title: "Motors and arms",
    text: "Motor, propeller hub or arm failure caused by internal manufacturing defects",
  },
  {
    img: "/izicart/ew-flight-controller.jpg",
    title: "Flight controller",
    text: "Flight controller, ESC, IMU or sensor circuit failure",
  },
  {
    img: "/izicart/ew-remote-controller.jpg",
    title: "Remote controller",
    text: "Remote controller malfunction due to circuit defects",
  },
];

const ewFaqData = [
  {
    question: "What is a IZI Extended Warranty Plan?",
    answer:
      "The IZI Care Plan is a protection program that offers extended warranty and accidental damage coverage. For drones, it provides extended warranty support, while for other products it includes accidental protection. Both services also include maintenance support to help keep your product in good working condition. With IZI Care, you get added peace of mind knowing that accidents and unexpected issues are covered for the duration of your plan. Please refer to the Terms and Conditions for full details.",
  },
  {
    question: "Why should I add a Protega Product Protection?",
    // interim answer built from the hero copy until the final FAQ answers arrive
    answer:
      "Protection for your IZI drone after the manufacturer’s warranty ends — mechanical, electrical and component failures caused by manufacturing defects, for 1 or 2 years.",
  },
  {
    question: "What happens if I return my product?",
    answer:
      "If you return your product, IZICart returns the amount you paid for your product protection. Depending on how much time has passed since your purchase, you may receive a prorated refund, less any paid service.",
  },
  {
    question:
      "I haven’t received a refund for my protection even though I returned my product.",
    answer:
      "IZICart handles all transactions for product protection sales and refunds. For assistance with refunds, contact our Customer Care.",
  },
  {
    question: "Is this plan the same as warranty or insurance?",
    answer:
      "No. This is an optional extended warranty plan that begins after the manufacturer’s warranty expires. It provides continued coverage for mechanical and electrical breakdowns due to normal use. It is not insurance and does not cover accidental damage, liquid spills, loss, or theft.",
  },
];

export default function IziExtenedWarrantyTnc() {
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const lenis = new Lenis({
      duration: 1.6,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      smoothWheel: true,
      touchMultiplier: 2,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    const handleBeforeUnload = () => {
      sessionStorage.setItem("scrollY", String(window.scrollY));
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    // 👇 Check if it's a reload (type === 'reload')
    const navEntries = performance.getEntriesByType(
      "navigation"
    ) as PerformanceNavigationTiming[];
    const isReload = navEntries.length > 0 && navEntries[0].type === "reload";

    if (isReload) {
      const savedScrollY = sessionStorage.getItem("scrollY");
      if (savedScrollY !== null) {
        setTimeout(() => {
          window.scrollTo(0, parseFloat(savedScrollY));
        }, 100); // Wait for Lenis and DOM layout
      }
    }

    return () => {
      lenis.destroy();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useGSAP();

  return (
    <div className="tw-min-h-screen tw-bg-black tw-text-white">
      <Header />
      <main className="tw-relative">
        {/* hero section */}
        <PlanHero
          image="ew-hero-2.JPG"
          alt="IZI Extended Warranty Plan - Drone"
          titleLines={["IZI Extended", "Warranty Plan"]}
          descriptionLines={[
            "Protection for your IZI drone after the manufacturer’s warranty",
            "ends — mechanical, electrical and component failures caused",
            "by manufacturing defects, for 1 or 2 years.",
          ]}
          dim
          imagePosition="tw-object-center"
        />

        {/* brand section */}
        <BrandBar />

        {/* extended warranty protection */}
        <CoverageCards
          title="Extended Warranty Protection"
          subtitleLines={[
            "Covered for failures caused by manufacturing defects — after the manufacturer’s",
            "warranty ends, during normal personal use. Batteries and accessories are not covered.",
          ]}
          items={ewCoverageItems}
        />

        {/* value section */}
        <WarrantyValue />

        {/* how it works */}
        <ClaimsHowItWorks />

        {/* not covered */}
        <NotCovered
          subtitle="A few things sit outside the extended warranty. The Terms list everything in full."
          exclusions={[
            "Accidental, crash or liquid damage",
            "Wear and tear, including propeller damage",
            "Batteries and accessories",
            "Firmware modification or unauthorised repairs",
            "Faults that first appeared during the manufacturer’s warranty",
            "Theft, loss or commercial use",
          ]}
        />

        {/* how to buy */}
        <HowToBuyPlan planName="IZI Extended Warranty Plan" />

        {/* faq section */}
        <FAQSection
          title="IZI Extended Warranty Plan FAQs"
          data={ewFaqData}
          link="/izicart/extended/tnc"
        />

        {/* footer */}
        <Footer />
      </main>
    </div>
  );
}
